// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Users, Trophy, Clock, Target, Crosshair, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TournamentCreationDialog } from "@/components/admin/tournaments/TournamentCreationDialog";
import { BracketGenerationDialog } from "@/components/admin/tournaments/BracketGenerationDialog";
import { TournamentSchedulingDialog } from "@/components/admin/tournaments/TournamentSchedulingDialog";
import { TimeSlotManagementDialog } from "@/components/admin/tournaments/TimeSlotManagementDialog";
import { TeamSeedingDialog } from "@/components/admin/tournaments/TeamSeedingDialog";
import { MatchScoringDialog } from "@/components/admin/tournaments/MatchScoringDialog";

interface Tournament {
  id: string;
  event_id: string;
  name: string;
  tournament_type: string;
  age_group_id: string;
  division_name?: string;
  max_teams: number;
  seeding_type: string;
  status: string;
  created_at: string;
  events: {
    name: string;
    venue_id?: string;
  };
  event_age_groups: {
    name: string;
  };
  _count?: {
    tournament_brackets: number;
  };
  has_special_requests?: boolean;
  special_requests_count?: number;
}

const TournamentManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultEventId = searchParams.get('eventId') || undefined;
  
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBracketDialogOpen, setIsBracketDialogOpen] = useState(false);
  const [isSchedulingDialogOpen, setIsSchedulingDialogOpen] = useState(false);
  const [isTimeSlotDialogOpen, setIsTimeSlotDialogOpen] = useState(false);
  const [isSeedingDialogOpen, setIsSeedingDialogOpen] = useState(false);
  const [isScoringDialogOpen, setIsScoringDialogOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [tournamentTeams, setTournamentTeams] = useState<Array<{ id: string; team_name: string; seed_number: number }>>([]);

  const handleSpecialRequestsClick = (eventId: string, eventName: string) => {
    // Navigate to special requests page with event filter applied
    navigate(`/admin-dashboard/events/special-requests?eventFilter=${eventId}`);
    toast.success(`Viewing special requests for ${eventName}`);
  };

  const fetchTournaments = async () => {
    console.log("🏆 Fetching tournaments...");
    
    try {
      const { data, error } = await supabase
        .from("tournaments")
        .select(`
          *,
          events (name, venue_id),
          event_age_groups (name)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      console.log("📊 Raw tournament data:", data);

      // Get team counts and special requests for each tournament
      const tournamentsWithCounts = await Promise.all(
        (data || []).map(async (tournament) => {
          console.log(`🔍 Fetching team count for tournament: ${tournament.name} (ID: ${tournament.id})`);
          
          const { count, error: countError } = await supabase
            .from("tournament_brackets")
            .select("*", { count: "exact", head: true })
            .eq("tournament_id", tournament.id);

          if (countError) {
            console.error(`❌ Error fetching count for tournament ${tournament.id}:`, countError);
          } else {
            console.log(`📈 Tournament ${tournament.name} has ${count} teams`);
          }

          // Check for special requests in the associated event
          console.log(`🚨 Checking special requests for event: ${tournament.event_id}`);
          const { data: specialRequestsData, error: specialRequestsError } = await supabase
            .from("event_registration_responses")
            .select("id, special_requests")
            .eq("event_id", tournament.event_id)
            .not("special_requests", "is", null)
            .neq("special_requests", "");

          let hasSpecialRequests = false;
          let specialRequestsCount = 0;

          if (specialRequestsError) {
            console.error(`❌ Error fetching special requests for event ${tournament.event_id}:`, specialRequestsError);
          } else {
            hasSpecialRequests = specialRequestsData && specialRequestsData.length > 0;
            specialRequestsCount = specialRequestsData?.length || 0;
            console.log(`🚨 Event ${tournament.events?.name} has ${specialRequestsCount} special requests`);
          }

          return {
            ...tournament,
            _count: { tournament_brackets: count || 0 },
            has_special_requests: hasSpecialRequests,
            special_requests_count: specialRequestsCount,
          };
        })
      );

      console.log("✅ Final tournaments with counts:", tournamentsWithCounts);
      setTournaments(tournamentsWithCounts);
    } catch (error: any) {
      console.error("❌ Error fetching tournaments:", error);
      toast.error("Failed to load tournaments");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
  }, []);

  // Auto-open create dialog if eventId is in URL params
  useEffect(() => {
    if (defaultEventId && !isLoading) {
      setIsCreateDialogOpen(true);
    }
  }, [defaultEventId, isLoading]);

  const handleDelete = async (tournamentId: string) => {
    if (!confirm("Are you sure you want to delete this tournament? This will also delete all associated teams and matches.")) return;

    try {
      // First delete related tournament_brackets records
      const { error: bracketsError } = await supabase
        .from("tournament_brackets")
        .delete()
        .eq("tournament_id", tournamentId);

      if (bracketsError) throw bracketsError;

      // Then delete related tournament_matches records
      const { error: matchesError } = await supabase
        .from("tournament_matches")
        .delete()
        .eq("tournament_id", tournamentId);

      if (matchesError) throw matchesError;

      // Finally delete the tournament
      const { error: tournamentError } = await supabase
        .from("tournaments")
        .delete()
        .eq("id", tournamentId);

      if (tournamentError) throw tournamentError;

      toast.success("Tournament deleted successfully");
      fetchTournaments();
    } catch (error: any) {
      console.error("Error deleting tournament:", error);
      toast.error("Failed to delete tournament: " + (error.message || "Unknown error"));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "setup":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "completed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const handleGenerateBracket = async (tournament: Tournament) => {
    try {
      // Check if bracket already exists
      const { data: existingMatches, error: matchError } = await supabase
        .from("tournament_matches")
        .select("id")
        .eq("tournament_id", tournament.id)
        .limit(1);

      if (matchError) throw matchError;

      if (existingMatches && existingMatches.length > 0) {
        toast.error("Bracket already exists for this tournament");
        return;
      }

      // Get teams for this tournament
      const { data: teams, error: teamsError } = await supabase
        .from("tournament_brackets")
        .select("*")
        .eq("tournament_id", tournament.id)
        .order("seed_number");

      if (teamsError) throw teamsError;

      if (!teams || teams.length === 0) {
        toast.error("No teams found for this tournament");
        return;
      }

      setSelectedTournament(tournament);
      setTournamentTeams(teams);
      setIsBracketDialogOpen(true);
    } catch (error: any) {
      console.error("Error preparing bracket generation:", error);
      toast.error("Failed to prepare bracket generation");
    }
  };

  const getTournamentTypeDisplay = (type: string) => {
    switch (type) {
      case "single_elimination":
        return "Single Elimination";
      case "double_elimination":
        return "Double Elimination";
      case "round_robin":
        return "Round Robin";
      case "pool_play_playoffs":
        return "Pool Play + Playoffs";
      default:
        return type;
    }
  };

  const getSeedingTypeDisplay = (type: string) => {
    switch (type) {
      case "manual":
        return "Manual";
      case "random":
        return "Random";
      case "ranking_based":
        return "Ranking-based";
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tournament Management</h1>
          <p className="text-muted-foreground mt-2">
            Create and manage tournaments for your events
          </p>
        </div>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Tournament
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            Tournaments
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No tournaments yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first tournament to get started
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Tournament
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tournament Name</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Age Group</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Seeding</TableHead>
                  <TableHead>Teams</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tournaments.map((tournament) => (
                  <TableRow key={tournament.id}>
                    <TableCell className="font-medium">
                      {tournament.name}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{tournament.events?.name}</span>
                        {tournament.has_special_requests && (
                          <div 
                            className="flex items-center gap-1 cursor-pointer hover:bg-orange-100 rounded-md px-2 py-1 transition-colors"
                            onClick={() => handleSpecialRequestsClick(tournament.event_id, tournament.events?.name || 'Unknown Event')}
                            title={`Click to view ${tournament.special_requests_count} special request${tournament.special_requests_count !== 1 ? 's' : ''} for this event`}
                          >
                            <AlertCircle className="h-4 w-4 text-orange-500" />
                            <Badge 
                              variant="outline" 
                              className="bg-orange-50 text-orange-700 border-orange-200 text-xs hover:bg-orange-100"
                            >
                              {tournament.special_requests_count} Special Request{tournament.special_requests_count !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{tournament.division_name || tournament.event_age_groups?.name || 'No Division'}</TableCell>
                    <TableCell>
                      {getTournamentTypeDisplay(tournament.tournament_type)}
                    </TableCell>
                    <TableCell>
                      {getSeedingTypeDisplay(tournament.seeding_type)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>
                          {tournament._count?.tournament_brackets || 0}/{tournament.max_teams}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={getStatusColor(tournament.status)}
                      >
                        {tournament.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {tournament.status === "setup" && (
                          <>
                            {tournament._count?.tournament_brackets && tournament._count.tournament_brackets > 0 && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedTournament(tournament);
                                  setIsSeedingDialogOpen(true);
                                }}
                                className="flex items-center gap-1"
                              >
                                <Target className="h-4 w-4" />
                                Manage Seeding
                              </Button>
                            )}
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => handleGenerateBracket(tournament)}
                              className="flex items-center gap-1"
                            >
                              <Trophy className="h-4 w-4" />
                              Generate Bracket
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTournament(tournament);
                                setIsTimeSlotDialogOpen(true);
                              }}
                              className="flex items-center gap-1"
                            >
                              <Clock className="h-4 w-4" />
                              Time Slots
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTournament(tournament);
                                setIsCreateDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(tournament.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                         {tournament.status === "active" && (
                          <>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => {
                                console.log("🎯 Opening scoring dialog for tournament:", tournament.name);
                                setSelectedTournament(tournament);
                                setIsScoringDialogOpen(true);
                              }}
                              className="flex items-center gap-1"
                            >
                              <Crosshair className="h-4 w-4" />
                              Score Matches
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTournament(tournament);
                                setIsSchedulingDialogOpen(true);
                              }}
                              className="flex items-center gap-1"
                            >
                              Schedule Matches
                            </Button>
                            <Badge variant="default" className="bg-green-100 text-green-800">
                              Bracket Generated
                            </Badge>
                          </>
                        )}
                        
                        {/* Delete button available for all tournaments */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(tournament.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <TournamentCreationDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setSelectedTournament(null);
        }}
        onSuccess={() => {
          setIsCreateDialogOpen(false);
          setSelectedTournament(null);
          fetchTournaments();
        }}
        editTournament={selectedTournament}
        defaultEventId={defaultEventId}
      />

      {selectedTournament && (
        <BracketGenerationDialog
          isOpen={isBracketDialogOpen}
          onClose={() => {
            setIsBracketDialogOpen(false);
            setSelectedTournament(null);
            setTournamentTeams([]);
          }}
          onSuccess={() => {
            setIsBracketDialogOpen(false);
            setSelectedTournament(null);
            setTournamentTeams([]);
            fetchTournaments();
          }}
          tournament={selectedTournament}
          teams={tournamentTeams}
        />
      )}

      {selectedTournament && (
        <TournamentSchedulingDialog
          isOpen={isSchedulingDialogOpen}
          onClose={() => {
            setIsSchedulingDialogOpen(false);
            setSelectedTournament(null);
          }}
          tournament={selectedTournament}
        />
      )}

      {selectedTournament && (
        <TimeSlotManagementDialog
          isOpen={isTimeSlotDialogOpen}
          onClose={() => {
            setIsTimeSlotDialogOpen(false);
            setSelectedTournament(null);
          }}
          tournament={selectedTournament}
        />
      )}

      {selectedTournament && (
        <TeamSeedingDialog
          isOpen={isSeedingDialogOpen}
          onClose={() => {
            setIsSeedingDialogOpen(false);
            setSelectedTournament(null);
          }}
          onSuccess={() => {
            setIsSeedingDialogOpen(false);
            fetchTournaments();
          }}
          tournament={selectedTournament}
        />
      )}

      {selectedTournament && (
        <MatchScoringDialog
          isOpen={isScoringDialogOpen}
          onClose={() => {
            setIsScoringDialogOpen(false);
            setSelectedTournament(null);
          }}
          tournament={selectedTournament}
        />
      )}
    </div>
  );
};

export default TournamentManagementPage;